import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Button, TextView, ChartTooltip } from '../../../../atoms';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { useWhatsappCounts } from '@api';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CallReceivedOutlinedIcon from '@mui/icons-material/CallReceivedOutlined';
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined';
import { CommonCard, SkeletonChart } from '@organisms';
import CustomizedYAxisTick from '../particles/CustomizedYAxisTick';
import CustomizedXAxisTick from '../particles/CustomizedXAxisTick';

interface callGetSrtTokenUsageProps {
  billingAccount?: BillingAccount;
  dates: { start: DateTime | null; end: DateTime | null };
  TemplateCount: number;
  InboundCount: number;
  OutboundCount: number;
}

const WhatappChart: React.FC<callGetSrtTokenUsageProps> = ({
  billingAccount,
  dates,
  TemplateCount,
  InboundCount,
  OutboundCount
}: callGetSrtTokenUsageProps) => {
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const startDate = dates.start?.toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });
  const endDate = dates.end?.toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });

  const { data: responseSrt, isFetching: isLoading } = useWhatsappCounts({
    from: startDate,
    to: endDate,
    billingAccountId: billingAccount?.id
  });

  const values = useMemo(() => {
    if (!responseSrt?.data?.data || !dates.start || !dates.end) return [];

    const counts = responseSrt.data.data;
    const dateMap = new Map<string, WhatsAppCount>();
    counts.forEach((item) => {
      if (item.date) {
        dateMap.set(item.date.substring(0, 10), item);
      }
    });

    const newValues: WhatsAppCount[] = [];
    let day = dates.start;
    const lastDay = dates.end;
    while (day <= lastDay) {
      const key = day.toFormat('yyyy-MM-dd');
      newValues.push(
        dateMap.get(key) || {
          date: day.toISO(),
          templateCount: 0,
          inboundCount: 0,
          outboundCount: 0
        }
      );
      day = day.plus({ day: 1 });
    }
    return newValues;
  }, [responseSrt, dates.start, dates.end]);

  const downloadLogs = async () => {
    setExportLoading(true);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE;
    const queryString = `${endDate ? `&endDate=${endDate}` : ''}${
      startDate ? `&startDate=${startDate}` : ''
    }${billingAccount ? `&billingAccountId=${billingAccount?.id}` : ``}`;
    const urlApi = `${apiBase}/whatsapp/csv-download?=${queryString}`;

    const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
    const hour = Number(zone[0]);
    const mint = Number(zone[1]);
    const totalMint: number = hour * 60 + mint;

    const fetchOptions: RequestInit = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        timezone: `${totalMint}`
      },
      method: 'GET'
    };

    const response: Response = await fetch(urlApi, fetchOptions);

    if (response.status == 200) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `WhatsApp ${DateTime.now().toFormat('LLL dd yyyy, hh:mm:ss')}.csv`
      );
      document.body.appendChild(link);
      link.click();
    } else {
      toast('Something went wrong');
    }
    setExportLoading(false);
  };

  return (
    <Box
      sx={{
        mt: 2
      }}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <TextView $fontSize={26} $fontWeight={700}>
          Whatsapp
        </TextView>
        <Button
          label="Export"
          onClick={downloadLogs}
          size="small"
          sx={{ mr: 2 }}
          disabled={exportLoading}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          gridAutoRows: '1fr',
          mt: 2,
          mb: 2
        }}
      >
        <CommonCard
          title="Template"
          count={TemplateCount}
          icon={<ArticleOutlinedIcon />}
          accent="#086BDE"
        />
        <CommonCard
          title="Inbound"
          count={InboundCount}
          icon={<CallReceivedOutlinedIcon />}
          accent="#F5CB5C"
        />
        <CommonCard
          title="Outbound"
          count={OutboundCount}
          icon={<CallMadeOutlinedIcon />}
          accent="#fe4a49"
        />
      </Box>
      {isLoading && !responseSrt?.data ? (
        <SkeletonChart height={300} />
      ) : (
      <Box
        mt={1}
        sx={{
          backgroundColor: 'rgba(52, 130, 207, 0.06)',
          border: '1px solid rgba(52, 130, 207, 0.15)',
          pt: 2,
          borderRadius: 2,
          height: 300
        }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={values}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              interval={0}
              height={80}
              tick={(props) => CustomizedXAxisTick(props)}
            />
            <YAxis tick={(props) => CustomizedYAxisTick(props)} />
            <Tooltip
              itemSorter={(item) => {
                switch (item.dataKey) {
                  case 'templateCount':
                    return 0;
                  case 'inboundCount':
                    return 1;
                  case 'outboundCount':
                    return 1;
                  default:
                    return 2;
                }
              }}
              content={
                <ChartTooltip
                  nameMap={{
                    templateCount: 'Template',
                    inboundCount: 'Inbound',
                    outboundCount: 'Outbound'
                  }}
                  showTotal
                  filterZero
                />
              }
              cursor={{ fill: 'rgba(43, 34, 88, 0.06)' }}
            />
            <Bar dataKey="templateCount" stackId="a" fill="#086BDE" />
            <Bar dataKey="inboundCount" stackId="a" fill="#F5CB5C" />
            <Bar dataKey="outboundCount" stackId="a" fill="#fe4a49" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      )}
    </Box>
  );
};

export default WhatappChart;
